import { useEffect, useRef, useState } from 'react';

import 'firebase/compat/firestore';
import {
  DocumentData,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import moment from 'moment';

import {
  setUserPropertyBillingCycle,
  setUserPropertyCampaignsGenerated,
  setUserPropertyConversionStage,
} from './analytics/analytics';
import { trackSentryError } from './analytics/sentry';
import { firebaseTimestampToDate } from './dateUtils';
import { firestoreDb } from './firebaseUtils';
import {
  Campaign,
  CodeDetails,
  Item,
  LoadingStatus,
  Plan,
  Product,
  StructuredItem,
} from './types';

const DB_PATH_USERS = 'users';
const DB_PATH_CODES = 'partners/appsumo/codes';
const DB_PATH_CAMPAIGNS = 'campaigns';
const DB_PATH_PRODUCTS = 'products';
const DB_PATH_ITEMS = 'items';
const DB_PATH_PLANS = 'plans/v2/items';

export const useListenUserChanges = (userId?: string) => {
  const [dbUser, setDbUser] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [analyticsProperties, setAnalyticsProperties] = useState<Record<
    string,
    any
  > | null>(null);

  // Keep the latest analytics properties to compare inside snapshot callbacks without re-subscribing
  const analyticsPropsRef = useRef<Record<string, any> | null>(null);

  // Function to update analytics from Firestore changes
  const updateAnalyticsFromFirestore = (newAnalyticsProps: Record<string, any>) => {
    try {
      // Update campaigns_generated if it changed
      if (typeof newAnalyticsProps.campaigns_generated === 'number') {
        setUserPropertyCampaignsGenerated(newAnalyticsProps.campaigns_generated);
      }

      // Update conversion_stage if it changed
      if (newAnalyticsProps.conversion_stage) {
        setUserPropertyConversionStage(newAnalyticsProps.conversion_stage);
      }

      // Update billing_cycle if it changed
      if (newAnalyticsProps.billing_cycle) {
        setUserPropertyBillingCycle(newAnalyticsProps.billing_cycle);
      }

      console.log(
        '[App | Analytics] Updated properties from Firestore | Raw data:',
        newAnalyticsProps
      );
    } catch (error) {
      console.warn(
        '[App | Analytics] Failed to update properties from Firestore:',
        error
      );
    }
  };

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setDbUser(null);
      setAnalyticsProperties(null);
      analyticsPropsRef.current = null;
      setError(new Error('No user ID provided'));
      return;
    }

    const userDocRef = doc(firestoreDb, DB_PATH_USERS, userId);
    const analyticsPropertiesRef = doc(
      firestoreDb,
      DB_PATH_USERS,
      userId,
      'analytics',
      'properties'
    );

    // Listen to both user document and analytics properties
    const unsubscribeUser = onSnapshot(
      userDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const userData = snapshot.data();
          setDbUser(userData);
        } else {
          setError(new Error('User document does not exist'));
        }
        setLoading(false);
      },
      (err) => {
        trackSentryError('Failed to listen to user changes', { userId, error: err });
        setError(err);
        setLoading(false);
      }
    );

    // Listen to analytics properties in the new structure
    const unsubscribeAnalytics = onSnapshot(
      analyticsPropertiesRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const newAnalyticsProperties = snapshot.data() || {};

          const previous = analyticsPropsRef.current;
          const hasChanged =
            JSON.stringify(newAnalyticsProperties) !== JSON.stringify(previous);

          console.log(
            '[App | Analytics] Analytics properties document updated | Raw analytics data:',
            {
              userId,
              timestamp: new Date().toISOString(),
              previousProperties: previous,
              newProperties: newAnalyticsProperties,
              hasChanged,
            }
          );

          if (hasChanged) {
            // Update state and ref
            setAnalyticsProperties(newAnalyticsProperties);
            const hadPrevious = previous !== null;
            analyticsPropsRef.current = newAnalyticsProperties;

            // Only update analytics if we have previous properties (avoid initial load)
            if (hadPrevious) {
              updateAnalyticsFromFirestore(newAnalyticsProperties);
            }
          }
        } else {
          // Analytics properties don't exist yet - this is normal for new users
          console.log(
            '[App | Analytics] Analytics properties document does not exist for user:',
            userId
          );
          setAnalyticsProperties(null);
          analyticsPropsRef.current = null;
        }
      },
      (err) => {
        trackSentryError('Failed to listen to analytics properties changes', {
          userId,
          error: err,
        });
        console.warn(
          '[App | Analytics] Failed to listen to analytics properties:',
          err
        );
        // Don't set global error for analytics - user document is more important
      }
    );

    // Clean up subscriptions on unmount
    return () => {
      unsubscribeUser();
      unsubscribeAnalytics();
    };
  }, [userId]);

  return { dbUser, loading, error, analyticsProperties };
};

/**
 * Hook to listen to purchase events for a specific user
 * Events are stored in /users/{userId}/analytics/events/purchase collection
 */
export const useListenPurchaseEvents = (userId?: string) => {
  const [purchaseEvents, setPurchaseEvents] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setPurchaseEvents([]);
      setError(new Error('No user ID provided'));
      return;
    }

    const purchaseEventsCollectionRef = collection(
      firestoreDb,
      DB_PATH_USERS,
      userId,
      'analytics',
      'events',
      'purchase'
    );

    // Listen to purchase events, ordered by creation time
    const eventsQuery = query(
      purchaseEventsCollectionRef,
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(
      eventsQuery,
      (snapshot) => {
        const eventsList: DocumentData[] = [];
        snapshot.forEach((doc) => {
          eventsList.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setPurchaseEvents(eventsList);
        setLoading(false);

        console.log(
          `[App | Analytics] Purchase events updated | Count: ${eventsList.length} | Raw data:`,
          eventsList
        );
      },
      (err) => {
        trackSentryError('Failed to listen to purchase events', { userId, error: err });
        setError(err);
        setLoading(false);
        console.warn('[App | Analytics] Failed to listen to purchase events:', err);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [userId]);

  return { purchaseEvents, loading, error };
};

export const useUserCodes = (userId?: string): CodeDetails[] => {
  const [codes, setCodes] = useState<CodeDetails[]>([]);

  useEffect(() => {
    if (!userId) {
      setCodes([]); // Clears any existing codes if the userId is not set
      return; // Stop the effect if no userId is provided
    }

    // Reference to the user's codes collection in Firestore
    const codesCollectionRef = collection(firestoreDb, DB_PATH_CODES);

    // Create a query object for codes that belong to the user, filter for is_redeemed=true and is_refaunded=false
    const codesQuery = query(codesCollectionRef, where('user_id', '==', userId));

    // Subscribe to real-time updates with onSnapshot
    const unsubscribe = onSnapshot(
      codesQuery,
      (snapshot) => {
        const userCodes: CodeDetails[] = [];
        snapshot.forEach((doc) => {
          const documentData = doc.data();
          userCodes.push({
            code: doc.id,
            createdAt: documentData?.created_at
              ? moment(firebaseTimestampToDate(documentData?.created_at)).toDate()
              : null,
            isRedeemed: documentData.is_redeemed,
            isRefunded: documentData.is_refaunded,
            redeemedAt: documentData.redeemed_at
              ? moment(firebaseTimestampToDate(documentData.redeemed_at)).toDate()
              : null,
            refundedAt: documentData.refaunded_at
              ? moment(firebaseTimestampToDate(documentData.refaunded_at)).toDate()
              : null,
            userId: documentData.user_id,
          } as CodeDetails);
        });
        setCodes(userCodes); // Update state with the fetched codes
      },
      (error) => {
        trackSentryError('Error fetching user codes', { userId, error });
        console.error('Error fetching user codes:', error);
        setCodes([]); // Handle potential errors by setting codes to null
      }
    );

    //cleanup the listener when the component unmounts or userId changes
    return () => unsubscribe();
  }, [userId]); // Re-run the effect if userId changes

  return codes;
};

export const updateCampaign = async (
  campaignId: string,
  updateData: Partial<Campaign>
): Promise<void> => {
  try {
    const campaignRef = doc(firestoreDb, DB_PATH_CAMPAIGNS, campaignId);
    await updateDoc(campaignRef, {
      ...updateData,
      updated_at: serverTimestamp(),
    });
  } catch (error) {
    trackSentryError('Failed to update campaign', { campaignId, updateData, error });
    throw error;
  }
};

export const deleteCampaign = async (campaignId: string): Promise<void> => {
  try {
    const campaignRef = doc(firestoreDb, DB_PATH_CAMPAIGNS, campaignId);

    // Delete all nested collections (e.g., items)
    const itemsCollectionRef = collection(campaignRef, DB_PATH_ITEMS);
    const itemsSnapshot = await getDocs(itemsCollectionRef);
    for (const itemDoc of itemsSnapshot.docs) {
      await deleteDoc(itemDoc.ref);
    }

    // Finally, delete the campaign document itself
    await deleteDoc(campaignRef);
  } catch (error) {
    trackSentryError('Failed to delete campaign', { campaignId, error });
    throw error;
  }
};

export const updateStructuredItem = async (
  campaignId: string,
  parentItemId: string,
  itemId: string,
  updateData: Partial<StructuredItem>
): Promise<void> => {
  try {
    const itemRef = doc(
      firestoreDb,
      DB_PATH_CAMPAIGNS,
      campaignId,
      DB_PATH_ITEMS,
      parentItemId,
      'structured_items',
      itemId
    );
    const dataToUpdate: { [key: string]: any } = {
      ...updateData,
      updated_at: serverTimestamp(),
    };

    if (dataToUpdate.status && typeof dataToUpdate.status !== 'string') {
      dataToUpdate.status = dataToUpdate.status.value;
    }

    await updateDoc(itemRef, dataToUpdate);
  } catch (error) {
    trackSentryError('Failed to update structured item', {
      campaignId,
      parentItemId,
      itemId,
      updateData,
      error,
    });
    throw error;
  }
};

export const deleteStructuredItemDb = async (
  campaignId: string,
  parentItemId: string,
  itemId: string
): Promise<void> => {
  try {
    const itemRef = doc(
      firestoreDb,
      DB_PATH_CAMPAIGNS,
      campaignId,
      DB_PATH_ITEMS,
      parentItemId,
      'structured_items',
      itemId
    );
    await deleteDoc(itemRef);
  } catch (error) {
    trackSentryError('Failed to delete structured item', {
      campaignId,
      parentItemId,
      itemId,
      error,
    });
    throw error;
  }
};

export const getProduct = async (productId: string): Promise<Product | null> => {
  try {
    const productRef = doc(firestoreDb, DB_PATH_PRODUCTS, productId);
    const productSnap = await getDoc(productRef);
    if (!productSnap.exists()) return null;
    return { id: productSnap.id, ...productSnap.data() } as Product;
  } catch (error) {
    trackSentryError('Failed to get product', { productId, error });
    throw error;
  }
};

export const useListenProductChanges = (productId?: string) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!productId) {
      setLoading(false);
      setProduct(null);
      return;
    }

    const productRef = doc(firestoreDb, 'products', productId);

    const unsubscribe = onSnapshot(
      productRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const productData = {
            id: snapshot.id,
            created_at: firebaseTimestampToDate(data.created_at),
            updated_at: firebaseTimestampToDate(data.updated_at),
            ...data,
          } as Product;
          setProduct(productData);
        } else {
          setProduct(null);
        }
        setLoading(false);
      },
      (err) => {
        trackSentryError('Failed to listen to product changes', {
          productId,
          error: err,
        });
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [productId]);

  return { product, loading, error };
};

export const useListenCampaignChanges = (campaignId?: string) => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [isUnknownResourceError, setIsUnknownResourceError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!campaignId) {
      setCampaign(null);
      setIsUnknownResourceError(false);
      return;
    }

    const handleError = (err: Error) => {
      trackSentryError('Failed to listen to campaign changes', {
        campaignId,
        error: err,
      });
      if (isMounted) {
        if (
          err.name === 'FirebaseError' &&
          (err.message.includes('evaluation error') ||
            err.message.includes('permission-denied') ||
            err.message.includes('false for'))
        ) {
          setIsUnknownResourceError(true);
          setCampaign(null);
        }
      }
    };

    const campaignRef = doc(firestoreDb, 'campaigns', campaignId);
    const itemsCollectionRef = collection(campaignRef, DB_PATH_ITEMS);

    const unsubscribeCampaign = onSnapshot(
      campaignRef,
      (snapshot) => {
        if (!isMounted) return;

        if (snapshot.exists()) {
          setIsUnknownResourceError(false);
          const data = snapshot.data();
          setCampaign((prevCampaign) => ({
            ...(data as Omit<
              Campaign,
              'id' | 'created_at' | 'updated_at' | 'status' | 'items'
            >),
            id: snapshot.id,
            created_at: firebaseTimestampToDate(data.created_at),
            updated_at: firebaseTimestampToDate(data.updated_at),
            status: data.status as LoadingStatus,
            items: prevCampaign?.items || [],
          }));
        } else {
          setCampaign(null);
          setIsUnknownResourceError(true);
        }
      },
      handleError
    );

    const unsubscribeItems = onSnapshot(
      itemsCollectionRef,
      (snapshot) => {
        if (!isMounted) return;

        setCampaign((prevCampaign) => {
          if (!prevCampaign) return null;

          const items: Item[] = snapshot.docs.map((doc) => {
            const itemData = doc.data();
            return {
              id: doc.id,
              ...itemData,
              created_at: firebaseTimestampToDate(itemData.created_at),
              updated_at: firebaseTimestampToDate(itemData.updated_at),
              versions:
                itemData.versions?.map((version: any) => ({
                  ...version,
                  created_at: firebaseTimestampToDate(version.created_at),
                  updated_at: firebaseTimestampToDate(version.updated_at),
                })) || [],
            } as Item;
          });

          return {
            ...prevCampaign,
            items,
          };
        });
      },
      handleError
    );

    return () => {
      isMounted = false;
      unsubscribeCampaign();
      unsubscribeItems();
    };
  }, [campaignId]);

  return { campaign, isUnknownResourceError };
};

export const useListenUserCampaigns = (userId?: string | null) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId?.length) {
      return;
    }

    const campaignsQuery = query(
      collection(firestoreDb, DB_PATH_CAMPAIGNS),
      where('user_id', '==', userId),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(
      campaignsQuery,
      (snapshot) => {
        try {
          const userCampaigns: Campaign[] = snapshot.docs.map((doc) => {
            const campaignData = doc.data();
            return {
              ...campaignData,
              id: doc.id,
              created_at: firebaseTimestampToDate(campaignData.created_at),
              updated_at: firebaseTimestampToDate(campaignData.updated_at),
              status: campaignData.status as LoadingStatus,
            } as Campaign;
          });

          setCampaigns(userCampaigns);
          setLoading(false);
          setError(null);
        } catch (err) {
          trackSentryError('Error fetching campaigns', { userId, error: err });
          console.error('Error fetching campaigns:', err);
          setError(err instanceof Error ? err : new Error('Unknown error occurred'));
          setLoading(false);
        }
      },
      (err) => {
        trackSentryError('Error in campaigns snapshot', { userId, error: err });
        console.error('Error in campaigns snapshot:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  return { campaigns, loading, error };
};

export const useUserPlan = (planCode?: string) => {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!planCode) {
      setPlan(null);
      setLoading(false);
      return;
    }

    const plansRef = collection(firestoreDb, DB_PATH_PLANS);
    const planQuery = query(plansRef, where('code', '==', planCode));

    const unsubscribe = onSnapshot(
      planQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const planData = snapshot.docs[0].data();
          setPlan({
            id: snapshot.docs[0].id,
            name: planData.name,
            code: planData.code,
            interval: planData.interval,
            credits: planData.credits,
            isRenewableCredits: planData.isRenewableCredits,
            price: planData.price,
            created_at: firebaseTimestampToDate(planData.created_at),
            updated_at: firebaseTimestampToDate(planData.updated_at),
          });
        } else {
          setPlan(null);
        }
        setLoading(false);
      },
      (error) => {
        trackSentryError('Error fetching plan', { planCode, error });
        console.error('Error fetching plan:', error);
        setPlan(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [planCode]);

  return { plan, loading };
};

export const setUpdatesBannerClosed = async (userId?: string, bannerName?: string) => {
  if (userId) {
    try {
      const userDocRef = doc(firestoreDb, DB_PATH_USERS, userId);
      await updateDoc(userDocRef, {
        [`flags.banners.${bannerName}_closed`]: true,
      });
    } catch (error) {
      trackSentryError('setUpdatesBannerClosed :: Error updating user flags', {
        userId,
        bannerName,
        error,
      });
      console.error('setUpdatesBannerClosed :: Error updating user flags:', error);
    }
  } else {
    trackSentryError('setUpdatesBannerClosed :: No user ID provided', { bannerName });
    console.error('setUpdatesBannerClosed :: No user ID provided');
  }
};

export const setUpdatesBannerClicked = async (userId?: string, bannerName?: string) => {
  if (userId) {
    try {
      const userDocRef = doc(firestoreDb, DB_PATH_USERS, userId);
      await updateDoc(userDocRef, {
        [`flags.banners.${bannerName}_clicked`]: true,
      });
    } catch (error) {
      trackSentryError('setUpdatesBannerClicked :: Error updating user flags', {
        userId,
        bannerName,
        error,
      });
      console.error('setUpdatesBannerClicked :: Error updating user flags:', error);
    }
  } else {
    trackSentryError('setUpdatesBannerClicked :: No user ID provided', { bannerName });
    console.error('setUpdatesBannerClicked :: No user ID provided');
  }
};

export const useListenStructuredItems = (
  campaignId?: string,
  parentItemId?: string
) => {
  const [items, setItems] = useState<StructuredItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!parentItemId || !campaignId) {
      setLoading(false);
      setItems([]);
      return;
    }

    const itemsSubcollectionRef = collection(
      firestoreDb,
      'campaigns',
      campaignId,
      'items',
      parentItemId,
      'structured_items'
    );
    const q = query(itemsSubcollectionRef, orderBy('created_at', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetchedItems: StructuredItem[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            created_at: firebaseTimestampToDate(data.created_at),
            updated_at: firebaseTimestampToDate(data.updated_at),
            versions:
              data.versions?.map((version: any) => ({
                ...version,
                created_at: firebaseTimestampToDate(version.created_at),
                updated_at: firebaseTimestampToDate(version.updated_at),
              })) || [],
          } as StructuredItem;
        });

        // Ensure deterministic ordering by order_index, fallback to created_at
        fetchedItems.sort((a, b) => {
          const ai = a.order_index ?? Number.MAX_SAFE_INTEGER;
          const bi = b.order_index ?? Number.MAX_SAFE_INTEGER;
          if (ai !== bi) return ai - bi;
          const at = (
            a.created_at instanceof Date ? a.created_at.getTime() : 0
          ) as number;
          const bt = (
            b.created_at instanceof Date ? b.created_at.getTime() : 0
          ) as number;
          return at - bt;
        });

        setItems(fetchedItems);
        setLoading(false);
      },
      (err) => {
        trackSentryError('Error fetching structured items', {
          campaignId,
          parentItemId,
          error: err,
        });
        console.error('Error fetching structured items:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [campaignId, parentItemId]);

  return { items, loading, error };
};

export const setLegacyPreviewBannerDismissed = async (
  userId?: string,
  tabName?: string,
  campaignId?: string
) => {
  if (userId && tabName && campaignId) {
    try {
      const userDocRef = doc(firestoreDb, DB_PATH_USERS, userId);
      await updateDoc(userDocRef, {
        [`flags.banners.legacy-preview.${tabName}.${campaignId}_dismissed`]: true,
      });
    } catch (error) {
      trackSentryError('setLegacyPreviewBannerDismissed :: Error updating user flags', {
        userId,
        tabName,
        campaignId,
        error,
      });
      console.error(
        'setLegacyPreviewBannerDismissed :: Error updating user flags:',
        error
      );
    }
  } else {
    trackSentryError('setLegacyPreviewBannerDismissed :: Missing required parameters', {
      userId,
      tabName,
      campaignId,
    });
    console.error('setLegacyPreviewBannerDismissed :: Missing required parameters');
  }
};

export const setLegacyPreviewBannerClicked = async (
  userId?: string,
  tabName?: string,
  campaignId?: string
) => {
  if (userId && tabName && campaignId) {
    try {
      const userDocRef = doc(firestoreDb, DB_PATH_USERS, userId);
      await updateDoc(userDocRef, {
        [`flags.banners.legacy-preview.${tabName}.${campaignId}_clicked`]: true,
      });
    } catch (error) {
      trackSentryError('setLegacyPreviewBannerClicked :: Error updating user flags', {
        userId,
        tabName,
        campaignId,
        error,
      });
      console.error(
        'setLegacyPreviewBannerClicked :: Error updating user flags:',
        error
      );
    }
  } else {
    trackSentryError('setLegacyPreviewBannerClicked :: Missing required parameters', {
      userId,
      tabName,
      campaignId,
    });
    console.error('setLegacyPreviewBannerClicked :: Missing required parameters');
  }
};
